import { BUDGETS_TYPES, COMPARISON_FIELD_OPTIONS, FILTER_COMPONENT_TYPES, getLeadTypeById, LEAD_TYPES_OPTIONS, NAME_OF_PRODUCT_TYPES, PRODUCT_TYPES_OPTIONS, REPORTING_TIMEZONE } from "helpers/sf_constants"
import { forColumnHeaderWrapper, percentageWithRatingFormatter, SfDollarStringFormatter, sortStringAsFloat, getIANATimezoneStringForAbbreviation, printNameOrEmail, SfPercentStringFormatter } from "helpers/sf_utils"
import { capitalize } from "lodash"
import { createOption, DATE_RANGE_OPTIONS, createLabelWithDateRange } from "components/Common/DateRangePicker"

export const ADMIN_CAMPAIGN_FULFILLMENT_FILTERS = {
    TIMEFRAME: 'received',
    ACCOUNT_ID: "account_id",
    CAMPAIGN_NAME: "campaign_name",
    BUDGET_TYPE: 'budget_type',
    PRODUCT_TYPE: 'product_type',
    SORT_BY: 'sort_by',
    ACCOUNT_MANAGER: 'account_manager',
    INSURANCE_TYPE: 'insurance_type',
    SHARED_LEAD_TYPE: 'shared_lead_type'
}

//Special cases where we need to pass selected_value as one more attribute
export const ADMIN_CAMPAIGN_FULFILLMENT_FILTER_SPECIAL_CASES = [
    ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.ACCOUNT_ID,
]

export const BUDGET_TYPE_OPTIONS = [
    { label: 'Volume', value: 'volume' },
    { label: 'Dollar', value: 'dollar' },
]

export const ADMIN_CAMPAIGN_FULFILLMENT_SORT_OPTIONS = [
    { label: 'Total Spent', value: 'campaign_spends.dollar_amt' },
    { label: 'Campaign Fulfillment', value: 'campaign_fulfillment' },
    { label: 'Account Fulfillment', value: 'account_fulfillment' }
]

export const SHARED_LEAD_TYPE_OPTIONS = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
]

export const getAdminCampaignFulfillmentFilterObjects = (accountManagerOptions = []) => {
    const employeesOptions = (accountManagerOptions || [])
        .map(employee => ({
            label: printNameOrEmail(employee?.first_name, employee?.last_name, employee?.email),
            value: employee.id
        }));

    const ianaTz = getIANATimezoneStringForAbbreviation(REPORTING_TIMEZONE)
    const defaultTodayOption = createOption(
        DATE_RANGE_OPTIONS.TODAY,
        new Date(),
        ianaTz
    );

    // Format the label to show "Today: [date]" format like "Yesterday: [date]"
    const formattedTodayOption = {
        ...defaultTodayOption,
        label: createLabelWithDateRange(
            {
                label: defaultTodayOption.label,
                startDate: defaultTodayOption.value.startDate,
                endDate: defaultTodayOption.value.endDate
            },
            ianaTz
        )
    };

    return {
        [ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.ACCOUNT_ID]: {
            order: 1,
            comparator_value: "",
            value: COMPARISON_FIELD_OPTIONS[2],
            label: "Account",
            name: ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.ACCOUNT_ID,
            component_type: FILTER_COMPONENT_TYPES.COMPARATOR,
            options: COMPARISON_FIELD_OPTIONS,
        },
        [ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.PRODUCT_TYPE]: {
            order: 2,
            comparator_value: "",
            value: "",
            label: "Product Type",
            name: ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.PRODUCT_TYPE,
            component_type: FILTER_COMPONENT_TYPES.MULTI_SELECT,
            options: PRODUCT_TYPES_OPTIONS,
        },
        [ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.BUDGET_TYPE]: {
            order: 3,
            comparator_value: "",
            value: "",
            label: "Budget Type",
            name: ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.BUDGET_TYPE,
            component_type: FILTER_COMPONENT_TYPES.SINGLE_SELECT,
            options: BUDGET_TYPE_OPTIONS,
        },
        [ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.TIMEFRAME]: {
            order: 4,
            comparator_value: "",
            value: formattedTodayOption,
            label: "Timeframe",
            name: ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.TIMEFRAME, // "received",
            component_type: FILTER_COMPONENT_TYPES.DATE_RANGE,
            single_date_selection: true,
            is_active: true,
        },
        [ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.SORT_BY]: {
            order: 5,
            comparator_value: "",
            value: ADMIN_CAMPAIGN_FULFILLMENT_SORT_OPTIONS[0],
            label: "Sort By",
            name: ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.SORT_BY,
            component_type: FILTER_COMPONENT_TYPES.SINGLE_SELECT,
            options: ADMIN_CAMPAIGN_FULFILLMENT_SORT_OPTIONS,
        },
        [ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.ACCOUNT_MANAGER]: {
            order: 6,
            comparator_value: "",
            value: "",
            label: "Account Manager",
            name: ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.ACCOUNT_MANAGER,
            component_type: FILTER_COMPONENT_TYPES.SINGLE_SELECT,
            options: employeesOptions,
        },
        [ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.INSURANCE_TYPE]: {
            order: 7,
            comparator_value: "",
            value: "",
            label: "Insurance Type",
            name: ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.INSURANCE_TYPE,
            component_type: FILTER_COMPONENT_TYPES.MULTI_SELECT,
            options: LEAD_TYPES_OPTIONS,
        },
        [ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.SHARED_LEAD_TYPE]: {
            order: 8,
            comparator_value: "",
            value: "",
            label: "Shared Lead Type",
            name: ADMIN_CAMPAIGN_FULFILLMENT_FILTERS.SHARED_LEAD_TYPE,
            component_type: FILTER_COMPONENT_TYPES.SINGLE_SELECT,
            options: SHARED_LEAD_TYPE_OPTIONS,
        }
    }
}

const nameIdFormatter = (name, id) => {
    return `${name} (#${id})`
}

const fulfillmentDataFormatter = (cellContent, csvExport = false) => {
    if (Number(cellContent) === -1) {
        return 'Open'
    }
    return csvExport ? SfPercentStringFormatter(Number(cellContent)) : percentageWithRatingFormatter(Number(cellContent))
}

const statusFormatter = (row) => {
    const { product_type, units, account_max_volume } = row;

    if (product_type === NAME_OF_PRODUCT_TYPES.CLICKS) {
        return `${units} Units`;
    }

    return account_max_volume ? `${units} out of ${account_max_volume}` : '--';
}

const dailyLimitFormatter = (_cellContent, row) => {
    const daily_max_type = row.daily_max_type;
    return daily_max_type === BUDGETS_TYPES.DOLLAR ? SfDollarStringFormatter(row.daily_max_dollar || 0) : daily_max_type === BUDGETS_TYPES.VOLUME ? row.daily_max_volume : capitalize(BUDGETS_TYPES.OPEN)
}

const accountLimitFormatter = (_cellContent, row) => {
    const account_max_type = row.account_max_type;
    return account_max_type === BUDGETS_TYPES.OPEN ? capitalize(BUDGETS_TYPES.OPEN) : row.account_max_volume
}

const dailyLimitSortFunc = (_a, _b, order, _dataField, rowA, rowB) => {
    // Get numeric values based on type
    const getDailyLimitValue = (row) => {
        if (row.daily_max_type === BUDGETS_TYPES.DOLLAR) {
            return parseFloat(row.daily_max_dollar) || 0;
        } else if (row.daily_max_type === BUDGETS_TYPES.VOLUME) {
            return parseFloat(row.daily_max_volume) || 0;
        } else {
            // OPEN type - treat as very large number to sort last
            return Number.MAX_SAFE_INTEGER;
        }
    };

    const valueA = getDailyLimitValue(rowA);
    const valueB = getDailyLimitValue(rowB);

    if (order === 'asc') {
        return valueA - valueB;
    }
    return valueB - valueA;
}

const accountLimitSortFunc = (_a, _b, order, _dataField, rowA, rowB) => {
    // Get numeric values
    const getAccountLimitValue = (row) => {
        if (row.account_max_type === BUDGETS_TYPES.OPEN) {
            // OPEN type - treat as very large number to sort last
            return Number.MAX_SAFE_INTEGER;
        }
        return parseFloat(row.account_max_volume) || 0;
    };

    const valueA = getAccountLimitValue(rowA);
    const valueB = getAccountLimitValue(rowB);

    if (order === 'asc') {
        return valueA - valueB;
    }
    return valueB - valueA;
}

const insuranceTypeFormatter = (cellContent, row) => {
    // Handle both insurance_type (ID) and lead_type_id
    const leadTypeId = row.insurance_type || row.lead_type_id;
    if (leadTypeId) {
        const leadType = getLeadTypeById(leadTypeId);
        return leadType ? capitalize(leadType.name) : '--';
    }
    // If it's already a string/name
    return cellContent ? capitalize(cellContent) : '--';
}

const sharedLeadTypeFormatter = (cellContent, row) => {
    // Handle both shared_lead_type and shared field names
    // Backend may return string "true"/"false" or boolean true/false
    const sharedValue = row.shared_lead_type !== undefined ? row.shared_lead_type : (row.shared !== undefined ? row.shared : cellContent);
    
    // Convert string "true"/"false" to boolean, or use boolean value directly
    let isShared;
    if (typeof sharedValue === 'string') {
        isShared = sharedValue.toLowerCase() === 'true';
    } else {
        isShared = Boolean(sharedValue);
    }
    
    return isShared ? 'Yes' : 'No';
}

const campaignNameSortFunc = (a, b, order, _dataField, rowA, rowB) => {
    const labelA = String((a ?? rowA?.campaign_name) ?? '').trim();
    const labelB = String((b ?? rowB?.campaign_name) ?? '').trim();
    const cmp = labelA.localeCompare(labelB, undefined, { sensitivity: 'base', numeric: true });
    return order === 'asc' ? cmp : -cmp;
};

export const adminCampaignFulFillmentColumns = (onClickUserLogin, isClientView = false, hideTotalSpent = false, onSortChange = null) => {
    const sortProps = onSortChange ? { onSort: onSortChange } : {};
    return [
        {
            dataField: "account_id",
            text: "Account",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            formatter: (cellContent, row) => {
                return (
                    <div className="d-flex align-items-center">
                        <a onClick={() => onClickUserLogin(cellContent)} className="text-sfblue">
                            {nameIdFormatter(row.account_name, cellContent)}
                        </a>
                    </div>
                )
            },
            csvFormatter: (cellContent, row) => nameIdFormatter(row.account_name, cellContent),
            hidden: isClientView,
            csvExport: !isClientView,
        },
        {
            dataField: "campaign_name",
            text: "Campaign",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            sortFunc: campaignNameSortFunc,
            formatter: (cellContent, row) =>
                nameIdFormatter(String(cellContent ?? '').trim(), row.campaign_id),
            csvFormatter: (cellContent, row) =>
                nameIdFormatter(String(cellContent ?? '').trim(), row.campaign_id),
            hidden: isClientView,
            csvExport: !isClientView,
        },
        {
            dataField: "product_type",
            text: "Product Type",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            formatter: (cell, _row) => capitalize(cell),
            csvFormatter: (cell) => capitalize(cell),
            hidden: isClientView,
            csvExport: !isClientView,
        },
        {
            dataField: "insurance_type",
            text: "Insurance Type",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            formatter: insuranceTypeFormatter,
            csvFormatter: (cellContent, row) => insuranceTypeFormatter(cellContent, row),
            hidden: isClientView,
            csvExport: !isClientView,
        },
        {
            dataField: "shared_lead_type",
            text: "Shared LeadType",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            formatter: sharedLeadTypeFormatter,
            csvFormatter: (cellContent, row) => sharedLeadTypeFormatter(cellContent, row),
            hidden: isClientView,
            csvExport: !isClientView,
        },
        {
            dataField: "dt",
            text: "Date",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            hidden: !isClientView
        },
        {
            dataField: "budget_type",
            text: "Budget Type",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
        },
        {
            dataField: "dollar_amt",
            text: "Total Spent",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            sortFunc: sortStringAsFloat,
            formatter: SfDollarStringFormatter,
            csvFormatter: SfDollarStringFormatter,
            hidden: hideTotalSpent,
            csvExport: !hideTotalSpent,
        },
        {
            // Unique dataField so sort caret is not shared with other composite columns (empty dataField merges in react-bootstrap-table)
            dataField: "sf_sort_daily_limit",
            text: 'Daily Limit',
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            formatter: dailyLimitFormatter,
            csvFormatter: (cellContent, row) => dailyLimitFormatter(cellContent, row),
            sortFunc: dailyLimitSortFunc,
        },
        {
            dataField: "campaign_fulfillment",
            text: "Campaign Fulfillment",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            formatter: (cellContent) => fulfillmentDataFormatter(cellContent),
            csvFormatter: (cellContent) => fulfillmentDataFormatter(cellContent, true),
            sortFunc: sortStringAsFloat
        },
        {
            dataField: "sf_sort_account_limit",
            text: 'Account Limit',
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            formatter: accountLimitFormatter,
            csvFormatter: (cellContent, row) => accountLimitFormatter(cellContent, row),
            sortFunc: accountLimitSortFunc,
        },
        {
            dataField: "account_fulfillment",
            text: "Account Fulfillment",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            formatter: (cellContent) => fulfillmentDataFormatter(cellContent),
            csvFormatter: (cellContent) => fulfillmentDataFormatter(cellContent, true),
            sortFunc: sortStringAsFloat
        },
        {
            dataField: "sf_status_display",
            text: "Status",
            headerFormatter: forColumnHeaderWrapper,
            sort: true,
            ...sortProps,
            formatter: (_cellContent, row) => statusFormatter(row),
            csvFormatter: (_cellContent, row) => statusFormatter(row),
            sortFunc: sortStringAsFloat,
            hidden: isClientView,
            csvExport: !isClientView,
        },
    ]
}